<template>
  <div v-if="isSelectable && (beingSearched || hasSearched)" class="settings-list-element-root row no-wrap">
    <mosta-checkbox
      style="display: flex !important;"
      :model-value="elementModel.Selected"
      @update:model-value="selectElement" />

    <div v-if="getSearchedName.length <= 0" class="full-width q-my-auto cursor-pointer" 
    style="margin-left: 6px !important;" @click="selectElement">
      {{ getElementName }}
    </div>
    <div v-else class="row justify-left full-width q-my-auto cursor-pointer" 
    style="margin-left: 6px !important;" @click="selectElement">
      <span v-for="(span, i) in getSearchedName" :key="i" :style="span.style">{{ span.text }}</span>
    </div>
  </div>

  <q-expansion-item
    v-else-if="!isSelectable && !isRoot && (beingSearched || hasSearched)"
    switch-toggle-side
    expand-icon-toggle
    :dense="true"
    :dense-toggle="true"
    :model-value="/*hasSearched || */true"
    expand-separator
    icon="expand_more"
    class="setting-item q-my-xs"
  >
    <template #header>
      <div v-if="getSearchedName.length <= 0" class="q-my-auto">
        {{ getElementName }}
      </div>
      <div v-else class="row justify-left full-width q-my-auto">
        <span v-for="(span, i) in getSearchedName" :key="i" :style="span.style">{{ span.text }}</span>
      </div>
    </template>
      <settings-list-element
        class="setting-selection--child"
        v-for="child in (element as ColumnSendComposite).ChildColumns"
        :element="child"
        :key="(child as ColumnListItem).compound_id"
        :input-index="(child as ColumnListItem).compound_id"
        :parent-id="(child as ColumnListItem).compound_id"
        :parent-name="getElementName"
        :parent-idx="(elementModel as ColumnListItem).compound_id"
        :is-root="false"
        :search-string="!beingSearched ? searchString : ''"
        :selector-popup-mode="selectorPopupMode"
        @changeSelection="onChildUpdate"
      />
  </q-expansion-item>
  <div v-else-if="!isSelectable && isRoot" class="settings-list-element-root row justify-left full-width no-wrap">
    <q-btn
      ref="selector_trigger_btn"
      class="q-my-none unset-items-center" flat dense round icon="add" size="sm" color="grey-8"
      :aria-label="$t('expand_parameters_selector')" style="margin-left: -5px !important;"
      @click="menuShowing = true"
    />
    <selector-popup
      v-model:visible="menuShowing"
      :trigger-el="$refs.selector_trigger_btn"
      :default-width="320"
      :min-width="260"
      :max-width="1200"
      :min-height="220"
      :max-height="700"
    >
      <div class="columns-selector-popup">
        <selector-search-header
          ref="columns_search_header"
          v-model="filter_string"
          :show-free-input="false"
          :show-close-button="true"
          @close="menuShowing = false"
        />
        <div class="columns-selector-subheader">
          <span class="columns-selector-subheader__label">{{ getElementName }}</span>
        </div>
        <div class="columns-selector-popup__list">
          <div
            v-for="row in popupFlatRows"
            :key="row.key"
            class="columns-popup-node__row-wrap"
          >
            <span
              v-for="level in row.depth"
              :key="'guide-' + level"
              class="columns-popup-node__guide"
              :style="{ left: treeGuideLineLeft(level - 1) + 'px' }"
            />
            <div
              class="columns-popup-node__row"
              :style="{ paddingLeft: `${row.depth * 22}px` }"
              draggable="false"
              @click.stop.prevent="onPopupRowClick(row)"
            >
            <span
              v-if="row.hasChildren"
              class="columns-popup-node__chevron"
              :class="{ 'columns-popup-node__chevron--closed': !popupIsExpanded(row.key) }"
              @click.stop.prevent="togglePopupExpand(row.key)"
            >
              <img :src="chevronDownSvg" class="columns-popup-node__chevron-icon" draggable="false" />
            </span>
            <span v-else class="columns-popup-node__chevron-placeholder"></span>

            <mosta-checkbox
              v-if="row.selectable"
              class="columns-popup-node__check"
              :model-value="!!(row.node as ColumnSendSelectable).Selected"
              @update:model-value="onPopupCheckboxUpdate(row, $event)"
              @click.stop
            />

            <div class="columns-popup-node__label">
              <span v-for="(span, i) in row.labelSpans" :key="i" :style="span.style">{{ span.text }}</span>
            </div>
            </div>
          </div>
        </div>
      </div>
    </selector-popup>
    <div class="col q-py-none">
      <!-- <div>{{ getElementName }}</div> -->
      <div v-if="getSearchedName.length <= 0" class="q-my-auto" style="margin-top: 2px;">
        {{ getElementName }}
      </div>
      <div v-else class="row justify-left full-width q-my-auto" style="margin-top: 2px;">
        <span v-for="(span, i) in getSearchedName" :key="i" :style="span.style">{{ span.text }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import { ColumnSend, ColumnSendComposite, ColumnSendSelectable } from '@/models/interfaces/entity-settings/columns/columns';
import { ColumnSendTypeDescriptor } from '@/models/etc/types';
import { ColumnListItem } from '../base-list/interfaces/ListTypes';
import { findRecursive, trimEllip } from '@/models/helpers/recursive';
import {
  buildRecursiveSearchIndex,
  buildHighlightSpans,
  searchInRecursiveIndex,
} from '@/components/common/basic/recursive-selector/refactored/composables/useRecursiveSearchMap';
import { flattenRecursiveTree } from '@/components/common/basic/recursive-selector/refactored/composables/useRecursiveTreeState';
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';
import SelectorPopup from '@/components/common/aux-selector/SelectorPopup.vue';
import SelectorSearchHeader from '@/components/common/aux-selector/SelectorSearchHeader.vue';
import chevronDownSvg from '@/assets/chevron-down.svg';
import { treeGuideLineLeft } from '@/components/common/tree/treeGuideLines';

// for matching string values that are in a wrong keyboard layout
const ruLayout = require('convert-layout/ru');

type PopupFlatRow = {
  key: string;
  node: ColumnSend;
  depth: number;
  hasChildren: boolean;
  selectable: boolean;
  labelSpans: Array<{ text: string; style: string }>;
};

@Component({
  name: 'SettingsListElement',
  components: { MostaCheckbox, SelectorPopup, SelectorSearchHeader },
})
export default class SettingsListElement extends Vue {
    @Prop({ default: {} as ColumnSend })
    element!: ColumnSend

    @Emit("changeSelection") emitSelectionChanged(arg: any) { return arg }

    @Prop({ default: false })
    isRoot!: boolean
    @Prop({ default: false })
    selectorPopupMode!: boolean
    @Prop({ default: '' })
    searchString!: string

    @Prop({ default: undefined })
    parentId!: number | undefined
    @Prop({ default: undefined })
    parentIdx!: string | undefined
    @Prop({ default: '' })
    parentName!: string
    @Prop({ default: -1 })
    inputIndex!: number
    
    // temporary data
    // @ts-ignore
    elementModel: ColumnSendSelectable = undefined
    old_value: boolean | undefined = undefined

    menuShowing: boolean = false
    filter_string: string = ''
    popupCollapsedKeys: Set<string> = new Set<string>()
    popupSearchCollapsedKeys: Set<string> = new Set<string>()
    chevronDownSvg = chevronDownSvg
    treeGuideLineLeft = treeGuideLineLeft

    created() {
        //
        this.elementModel = this.element as ColumnSendSelectable
        this.old_value = this.elementModel.Selected
    }

    @Watch('elementModel', { deep: true })
    onModelUpdate(arg: any, old_arg: any) {
      if (old_arg && (this.old_value === undefined || this.old_value !== arg.Selected)) {
        this.old_value = arg.Selected
        this.emitSelectionChanged(arg)
      }
    }

    // selection methods
  selectElement() {
    if (!this.elementModel || !this.isSelectable) { return }
    if (this.elementModel && !this.elementModel.Selected) {
      this.elementModel.Selected = true;
    } else {
      this.elementModel.Selected = false;
    }
  }

    onChildUpdate(arg: any) {
      this.emitSelectionChanged(arg)
    }

    togglePopupExpand(key: string) {
      if (this.popupCollapsedKeys.has(key)) {
        this.popupCollapsedKeys.delete(key);
        return;
      }

      // В поиске сначала даём пользователю «схлопнуть» авто/дефолтно раскрытую ветку.
      if (this.popupSearchResult.searchActive) {
        if (this.popupSearchCollapsedKeys.has(key)) {
          this.popupSearchCollapsedKeys.delete(key);
          return;
        }
        this.popupSearchCollapsedKeys.add(key);
        return;
      }

      this.popupCollapsedKeys.add(key);
    }

    get searchRegex() {
        if (!this.searchString) return null;
        return new RegExp('('+this.searchString
        .replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&').toLowerCase()
        .split(' ')
        .filter((s: string) => s !== '')
        .map((s: string) => {
            return '.*('+s+')'
        }).join('')+'.*)', 'gmi');
      }

      get getSearchedName(): Array<any> {
        if (!this.searchRegex || !this.searchString) {
          return []
        }

        const res = []
        const matches = []
        let mm
        const translatedName = ruLayout.toEn(this.getElementName)
        while ((mm = this.searchRegex.exec(this.getElementName))) {
          if (mm) {
            for (const r of mm) { (r != this.getElementName) && matches.push(r) }
          }
        }
        while ((mm = this.searchRegex.exec(translatedName))) {
          if (mm) {
            for (const r of mm) { (r != translatedName) && matches.push(ruLayout.fromEn(r)) }
          }
        }
        
        let newName = trimEllip(this.getElementName, 50);
        let lastIndex = 0;

        for (const match of matches) {
          let matchIndex = newName.toLowerCase().indexOf(match.toLowerCase(), lastIndex);

          while (matchIndex !== -1) {
            // Проверяем, есть ли текст перед совпадением
            if (lastIndex < matchIndex) {
              let beforeText = newName.substring(lastIndex, matchIndex);
              
              // Если перед совпадением есть пробел, добавляем его отдельно
              if (beforeText.endsWith(" ")) {
                res.push({ style: "white-space: pre;", text: beforeText.slice(0, -1) }); // Текст без последнего пробела
                res.push({ style: "white-space: pre;", text: "\u00A0" }); // Пробел в отдельном span
              } else {
                res.push({ style: "white-space: pre;", text: beforeText });
              }
            }

            // Добавляем совпадение с подсветкой
            res.push({
              style: "background-color: rgba(255, 184, 0, 0.4); border-radius: 2px; white-space: pre;",
              text: newName.substring(matchIndex, matchIndex + match.length),
            });

            lastIndex = matchIndex + match.length;

            // Если после совпадения идёт пробел, добавляем его отдельно
            if (newName[lastIndex] === " ") {
              res.push({ style: "white-space: pre;", text: "\u00A0" }); // Пробел в отдельном span
              lastIndex++;
            }

            matchIndex = newName.toLowerCase().indexOf(match.toLowerCase(), lastIndex);
          }
        }

        // Добавляем остаток строки (если есть)
        if (lastIndex < newName.length) {
          res.push({ style: "white-space: pre;", text: newName.substring(lastIndex) });
        }

        return res;
      }

    get isSelectable(): boolean {
      return this.element.Descriptor !== ColumnSendTypeDescriptor.Composite
    }

    get getElementName(): string {
      return (this.elementModel.Selected || !this.elementModel.Entity) ? 
      (this.element as ColumnListItem).compound_name : this.elementModel.Entity.Name
    }

    get popupRoots(): ColumnSend[] {
      if (!this.isRoot) return [];
      const roots = (this.element as ColumnSendComposite)?.ChildColumns;
      return Array.isArray(roots) ? roots : [];
    }

    popupGetChildren(node: ColumnSend): ColumnSend[] {
      const children = (node as ColumnSendComposite)?.ChildColumns;
      return Array.isArray(children) ? children : [];
    }

    popupNodeSeed(node: ColumnSend): string {
      const listNode = node as ColumnListItem;
      if (listNode?.compound_id !== undefined && listNode?.compound_id !== null) {
        return String(listNode.compound_id);
      }
      if (listNode?.compound_name) return String(listNode.compound_name);
      if (node?.Entity?.Id !== undefined && node?.Entity?.Id !== null) return String(node.Entity.Id);
      if (node?.Entity?.Name) return String(node.Entity.Name);
      return 'node';
    }

    popupNodeKey(node: ColumnSend, parentKey: string | null, index: number): string {
      const levelKey = `i${index}::${this.popupNodeSeed(node)}`;
      return parentKey ? `${parentKey}::${levelKey}` : `root::${levelKey}`;
    }

    popupNodeName(node: ColumnSend): string {
      const modelNode = node as ColumnSendSelectable;
      return (modelNode?.Selected || !modelNode?.Entity)
        ? (node as ColumnListItem).compound_name
        : (modelNode.Entity?.Name || '');
    }

    get popupSearchIndex() {
      return buildRecursiveSearchIndex<ColumnSend>({
        roots: this.popupRoots,
        getChildren: (node) => this.popupGetChildren(node),
        getKey: (node, parentKey, index) => this.popupNodeKey(node, parentKey, index),
        getText: (node) => this.popupNodeName(node),
      });
    }

    get popupSearchResult() {
      return searchInRecursiveIndex(this.popupSearchIndex, this.filter_string || '');
    }

    get popupAllExpandableKeys(): Set<string> {
      const keys = new Set<string>();
      this.popupSearchIndex.childrenByKey.forEach((children, key) => {
        if (children.length > 0) keys.add(key);
      });
      return keys;
    }

    popupIsExpanded(key: string): boolean {
      if (this.popupCollapsedKeys.has(key)) return false;
      if (this.popupSearchResult.searchActive && this.popupSearchCollapsedKeys.has(key)) return false;
      return this.popupAllExpandableKeys.has(key);
    }

    get popupExpandedKeys(): Set<string> {
      const expanded = new Set<string>();
      this.popupAllExpandableKeys.forEach((key) => {
        if (!this.popupCollapsedKeys.has(key)
          && !(this.popupSearchResult.searchActive && this.popupSearchCollapsedKeys.has(key))) {
          expanded.add(key);
        }
      });
      return expanded;
    }

    get popupFlatRows(): PopupFlatRow[] {
      const rows = flattenRecursiveTree<ColumnSend>({
        roots: this.popupRoots,
        index: this.popupSearchIndex,
        expandedKeys: this.popupExpandedKeys,
        autoExpandedKeys: this.popupSearchResult.autoExpandedKeys,
        searchActive: this.popupSearchResult.searchActive,
        visibleKeys: this.popupSearchResult.visibleKeys,
        getChildren: (node) => this.popupGetChildren(node),
        getKey: (node, parentKey, index) => this.popupNodeKey(node, parentKey, index),
        appendToChain: () => [],
      });

      return rows.map((row) => {
        const label = this.popupNodeName(row.node);
        return {
          key: row.key,
          node: row.node,
          depth: row.depth,
          hasChildren: row.hasChildren,
          selectable: row.node.Descriptor !== ColumnSendTypeDescriptor.Composite,
          labelSpans: buildHighlightSpans(label || '', this.popupSearchResult.tokens || []),
        };
      });
    }

    onPopupCheckboxUpdate(row: PopupFlatRow, value: any) {
      this.setPopupRowSelection(row, !!value);
    }

    onPopupRowClick(row: PopupFlatRow) {
      if (row.hasChildren) {
        this.togglePopupExpand(row.key);
        return;
      }
      if (!row.selectable) return;
      const selectable = row.node as ColumnSendSelectable;
      this.setPopupRowSelection(row, !selectable.Selected);
    }

    setPopupRowSelection(row: PopupFlatRow, selected: boolean) {
      const selectable = row.node as ColumnSendSelectable;
      selectable.Selected = selected;
      this.emitSelectionChanged(row.node);
    }

    // filtering in popup menu
    get hasSearched(): boolean {
      // if (!this.searchString) { return true }
      if ((this.element as ColumnSendComposite).ChildColumns) {
        const that = this
        
        const param = findRecursive(function a(element: any, el_to_find: any): any | undefined {
          if (!element || !el_to_find) return undefined

          if (element.ChildColumns) {
            if (element.Entity?.Name.toLowerCase().includes(el_to_find)
            || ruLayout.toEn(element.Entity?.Name).toLowerCase().includes(el_to_find.toLowerCase())) {
              return element;
            }
          }
          if (element.ChildColumns) {
            return findRecursive(a, el_to_find, element.ChildColumns)
          }
          return undefined
        }, (that.searchString.toLowerCase() || ''), (this.element as ColumnSendComposite).ChildColumns)

        if (param) {
          // console.log(param)
        }
        // true if Name suits or contains child
        return param ? true : false
      }

      return false
    }

    get beingSearched(): boolean {
      if (!this.searchString) { return true }
      return this.getElementName.toLowerCase().includes(this.searchString) ||
        ruLayout.toEn(this.getElementName).toLowerCase().includes(this.searchString.toLowerCase()) ? true : false
    }

    @Watch('inputItems', { deep: true })
    updateItems() {
        // remember state
        // @ts-ignore
    }

    @Watch('menuShowing')
    onMenuShowingChanged(val: boolean) {
      if (!val) return;
      this.filter_string = '';
      this.popupCollapsedKeys = new Set<string>();
      this.popupSearchCollapsedKeys = new Set<string>();
      this.$nextTick(() => {
        const header = this.$refs.columns_search_header as any;
        header?.focusField && header.focusField();
      });
    }

    @Watch('filter_string')
    onPopupFilterChanged() {
      if (this.popupSearchCollapsedKeys.size > 0) {
        this.popupSearchCollapsedKeys = new Set<string>();
      }
    }
}
</script>

<style lang="scss">
/* Зазор 12px от верха для каждого корневого SettingsListElement (selectable-чекбоксы
   и isRoot-блок «+ Поля названия»). Применяется ко всем включая первый — даёт 12px
   от предыдущего блока (последний SelectedListElement в SelectedSettingsList).
   q-expansion-item (рекурсивная выпадашка) класс не получает — её не трогаем. */
.settings-list-element-root {
  margin-top: 8px;
}

.setting-selection {
  &--child {
    margin-left: 26px !important;
  }
}

.setting-item {
  .q-item__section--avatar {
    padding-left: 0 !important;
  }
}

.columns-selector-popup {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;

  &__list {
    overflow: auto;
    max-height: 80vh;
    padding: 0 12px 8px 12px;
  }
}

.columns-selector-subheader {
  display: flex;
  height: 18px;
  padding: 0 12px;
  margin: 0 0 8px 0;
  justify-content: space-between;
  align-items: flex-end;

  &__label {
    color: var(--Text-color-text-secondary, #979797);
    font-family: Roboto, sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
  }
}

.columns-selector-popup {
  .columns-popup-node__row-wrap {
    position: relative;
  }

  .columns-popup-node__guide {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: #F2F2F2;
    pointer-events: none;
  }

  .columns-popup-node__row {
    display: flex;
    align-items: flex-start;
    width: 100%;
    min-height: 26px;
    padding: 4px 0;
    box-sizing: border-box;
    border-radius: 8px;
    cursor: pointer;
    user-select: none;
    box-shadow: none !important;

    &:hover {
      background-color: #f2f2f2;
    }
  }

  .columns-popup-node__row--active {
    background-color: #f2f2f2;
  }

  .columns-popup-node__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    min-width: 16px;
    height: 20px;
    margin-left: 8px;
    margin-right: 7px;
    cursor: pointer;
    user-select: none;
  }

  .columns-popup-node__chevron-placeholder {
    display: inline-flex;
    width: 16px;
    min-width: 16px;
    height: 20px;
    margin-left: 8px;
    margin-right: 7px;
    flex-shrink: 0;
  }

  .columns-popup-node__chevron-icon {
    width: 16px;
    height: 16px;
    transition: transform 0.12s ease;
  }

  .columns-popup-node__chevron--closed .columns-popup-node__chevron-icon {
    transform: rotate(-90deg);
  }

  .columns-popup-node__label {
    color: var(--Text-color-text_main, #4D4D4D);
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    white-space: normal;
    word-break: break-word;
  }

  .columns-popup-node__check {
    margin-top: 0;
    margin-right: 8px;
    flex-shrink: 0;
  }

  .columns-popup-node__children {
    width: 100%;
  }
}
</style>